import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import { Avatar, CardContent, Chip, Tab, Tabs } from '@mui/material';
// utils
import { fNumber } from 'src/utils/format-number';
// types
// components
import Editor from 'src/components/editor';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
//
import { setError } from 'src/redux/slices/error';
import ReviewItem from './review-item';
import MessageItem from './message-item';
import { useLocales } from '../../locales';
import {
  ClassesService,
  ClassFeedbackDto,
  ClazzDto,
  MailsService,
  MessageDto,
  ParticipationStatus,
  TextMessageService,
} from '../../api';
import { useDispatch } from '../../redux/store';
import { getClassMessages } from '../../redux/slices/classes';
import { fDate, fTime } from '../../utils/format-time';

// ----------------------------------------------------------------------

type Props = {
  clazz: ClazzDto;
  messages: MessageDto[];
  feedback: ClassFeedbackDto[];
};

export default function ClassHome({ clazz, messages, feedback }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const { t } = useLocales();

  const newSchema = Yup.object().shape({
    body: Yup.string().required('Body is required'),
    sendAsEmail: Yup.boolean(),
    sendAsPushNotification: Yup.boolean(),
  });

  const defaultValues = useMemo(
    () => ({
      body: '',
      sendAsEmail: false,
      sendAsPushNotification: true,
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(newSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await ClassesService.sendMessage({
        id: clazz.id!,
        body: {
          subject: `${clazz.title} - ${clazz.begins}`,
          body: data.body,
          sendAsEmail: false,
          sendAsPushNotification: true,
        },
      });

      reset();
      enqueueSnackbar(t('Message sent!'));

      dispatch(getClassMessages(clazz.id!));
    } catch (error) {
      dispatch(setError(error));
    }
  });

  const posts = ([] as any[]).concat(messages).concat(feedback);
  posts.sort((a, b) => b.createdDate!.toMillis() - a.createdDate!.toMillis());

  const members = useMemo(() => {
    const l =
      clazz.participants?.filter(
        (p) =>
          p.status !== ParticipationStatus.OptedOut &&
          p.status !== ParticipationStatus.OptedOutTooLate
      ) ?? [];

    return l.sort((a, b) => a.name!.localeCompare(b.name!));
  }, [clazz]);

  const newTextMessageSchema = Yup.object().shape({
    recipients: Yup.array().min(1),
    gymId: Yup.number(),
    body: Yup.string().required(t('Body is required')),
  });

  const textMessageDefaultValues = useMemo(
    () => ({
      recipients: members,
      body: '',
      gymId: clazz.gymId,
    }),
    [clazz, members]
  );

  const textMessageMethods = useForm({
    resolver: yupResolver(newTextMessageSchema),
    defaultValues: textMessageDefaultValues,
  });

  const onTextMessageSubmit = textMessageMethods.handleSubmit(async (data) => {
    try {
      await TextMessageService.create({
        body: {
          ...(data as any),
          recipientIds: data.recipients?.map((e) => e.accountId!),
          gymId: data.gymId,
        },
      });

      textMessageMethods.reset();
      enqueueSnackbar(t('Text message sent!'));
    } catch (error) {
      dispatch(setError(error));
    }
  });

  const newEmailSchema = Yup.object().shape({
    body: Yup.string().required(t('Body is required')),
    subject: Yup.string().required(t('Subject is required')),
    sendTo: Yup.array().required().min(1),
  });

  const emailDefaultValues = useMemo(
    () => ({
      body: '',
      subject: '',
      sendTo: members,
    }),
    [members]
  );

  const emailMethods = useForm({
    resolver: yupResolver(newEmailSchema),
    defaultValues: emailDefaultValues,
  });

  useEffect(() => emailMethods.reset(), [clazz, emailMethods]);

  const onEmailStateChange = (value: string, _delta: any, _source: any, _editor: any) => {
    emailMethods.setValue('body', value);
  };

  const emailContent = emailMethods.watch('body');

  const onEmailSubmit = emailMethods.handleSubmit(async (data) => {
    try {
      await MailsService.sendEmail({
        body: {
          html: data.body,
          subject: data.subject,
          sendTo: data.sendTo.map((m) => m.accountEmailAddress!),
        },
      });

      emailMethods.reset();
      enqueueSnackbar(t('Email sent!'));
    } catch (error) {
      dispatch(setError(error));
    }
  });

  const renderStats = (
    <Card sx={{ py: 3, textAlign: 'center', typography: 'h4' }}>
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
      >
        <Stack width={1}>
          {fNumber(clazz.capacity ?? 0)}
          <Box component="span" sx={{ color: 'text.secondary', typography: 'body2' }}>
            {t('Seats')}
          </Box>
        </Stack>

        <Stack width={1}>
          {fNumber(
            clazz.participants?.filter(
              (x) =>
                x.status === ParticipationStatus.OnTime || x.status === ParticipationStatus.SignedUp
            ).length ?? 0
          )}
          <Box component="span" sx={{ color: 'text.secondary', typography: 'body2' }}>
            {t('Participants')}
          </Box>
        </Stack>
      </Stack>
    </Card>
  );

  const renderAbout = (
    <Card>
      <CardHeader title={t('Time and place')} />

      <Stack spacing={2} sx={{ p: 3 }}>
        <Box sx={{ typography: 'body2' }}>{clazz.gym?.name}</Box>

        <Stack direction="row" spacing={2}>
          <Iconify icon="mdi:calendar-range" width={24} />

          <Box sx={{ typography: 'body2' }}>
            <Link variant="subtitle2" color="inherit">
              {`${fDate(clazz.begins)}`}
            </Link>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2}>
          <Iconify icon="mdi:clock" width={24} />

          <Box sx={{ typography: 'body2' }}>
            <Link variant="subtitle2" color="inherit">
              {t(`{{begins}} to {{ends}}`, {
                begins: fTime(clazz.begins),
                ends: fTime(clazz.ends),
              })}
            </Link>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2}>
          <Iconify icon="mingcute:location-fill" width={24} />

          <Box sx={{ typography: 'body2' }}>
            <Link variant="subtitle2" color="inherit">
              {`${clazz.gym?.address?.addressLine}, ${clazz.gym?.address?.postalNumber} ${clazz.gym?.address?.city}, ${clazz.gym?.address?.country}`}
            </Link>
          </Box>
        </Stack>
      </Stack>
    </Card>
  );

  const renderNotificationInput = (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <RHFTextField
        key={`notification-${clazz.id}`}
        name="body"
        label={t('Message')}
        multiline
        rows={4}
        sx={{
          mb: 3,
        }}
      />

      <Stack direction="row" alignItems="center" justifyContent="end">
        <Button
          type="submit"
          variant="contained"
          color="primary"
          loading={isSubmitting}
          endIcon={<Iconify icon="mdi:send" />}
        >
          {t('Send')}
        </Button>
      </Stack>
    </FormProvider>
  );

  const renderSmsInput = (
    <FormProvider methods={textMessageMethods} onSubmit={onTextMessageSubmit}>
      <RHFAutocomplete
        key={`sms-recipients-${clazz.id}`}
        name="recipients"
        multiple
        options={members}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name ?? '')}
        isOptionEqualToValue={(option, value) =>
          option.accountEmailAddress === value.accountEmailAddress
        }
        label={`${t('To')}`}
        renderValue={(value, getItemProps) => (
          <>
            {value.map((v) =>
              typeof v === 'string' ? (
                v
              ) : (
                <Chip
                  key={`chip-recipients-${v.id}`}
                  avatar={<Avatar alt={v.name} src={v.profileImageUrl} />}
                  label={v.name}
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
              )
            )}
          </>
        )}
        sx={{
          mb: 2,
        }}
      />
      <RHFTextField
        name="body"
        label={t('Text message')}
        multiline
        fullWidth
        rows={4}
        sx={{
          mb: 3,
        }}
      />
      <Stack direction="row" alignItems="end" justifyContent="end">
        <Button
          type="submit"
          variant="contained"
          color="primary"
          loading={textMessageMethods.formState.isSubmitting}
          endIcon={<Iconify icon="mdi:send" />}
        >
          {t('Send')}
        </Button>
      </Stack>
    </FormProvider>
  );

  const renderEmailInput = (
    <FormProvider methods={emailMethods} onSubmit={onEmailSubmit}>
      <Stack spacing={2}>
        <RHFAutocomplete
          key={`email-recipients-${clazz.id}`}
          name="sendTo"
          multiple
          options={members}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name ?? '')}
          isOptionEqualToValue={(option, value) =>
            option.accountEmailAddress === value.accountEmailAddress
          }
          label={`${t('To')}`}
          renderValue={(value, getItemProps) => (
            <>
              {value.map((v) =>
                typeof v === 'string' ? (
                  v
                ) : (
                  <Chip
                    key={`chip-recipients-${v.id}`}
                    avatar={<Avatar alt={v.name} src={v.profileImageUrl} />}
                    label={v.name}
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                )
              )}
            </>
          )}
        />

        <RHFTextField name="subject" label={t('Subject')} />

        <Editor simple id="reply-mail" value={emailContent} onChange={onEmailStateChange} />

        <Stack direction="row" alignItems="end" sx={{ width: 1 }} justifyContent="end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            endIcon={<Iconify icon="mdi:send" />}
            loading={emailMethods.formState.isSubmitting}
          >
            {t('Send')}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );

  function isFeedback(object: any): object is ClassFeedbackDto {
    return 'rating' in object;
  }

  const renderReviewItem = (r: ClassFeedbackDto) => <ReviewItem key={r.id} feedback={r} />;

  const renderMessageItem = (r: MessageDto) => <MessageItem key={r.id} message={r} />;

  const tabs = [
    {
      value: 'sms',
      label: t('SMS'),
      icon: <Iconify icon="material-symbols:sms-outline" width={24} />,
    },
    {
      value: 'notification',
      label: t('Notification'),
      icon: <Iconify icon="mdi:notifications-none" width={24} />,
    },
    {
      value: 'email',
      label: t('Email'),
      icon: <Iconify icon="mdi:email-outline" width={24} />,
    },
  ];

  const [currentTab, setCurrentTab] = useState(tabs[0].value);

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={3}>
          {renderStats}

          {renderAbout}
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <Stack spacing={3}>
          <Card>
            <Tabs value={currentTab} onChange={handleChangeTab}>
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  icon={tab.icon}
                  label={tab.label}
                  sx={{
                    pr: 2,
                    pl: 2,
                  }}
                />
              ))}
            </Tabs>
            <CardContent>
              {currentTab === 'notification' && renderNotificationInput}

              {currentTab === 'sms' && renderSmsInput}

              {currentTab === 'email' && renderEmailInput}
            </CardContent>
          </Card>

          {posts.map((r) => (isFeedback(r) ? renderReviewItem(r) : renderMessageItem(r)))}
        </Stack>
      </Grid>
    </Grid>
  );
}
